import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LoginPage() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    if (user) {
        setTimeout(() => navigate("/inbox", { replace: true }), 0);
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError("");
        setSubmitting(true);
        const res = await login(email, password);
        setSubmitting(false);
        if (!res.ok) {
            setError(res.error || "Credenciales inválidas");
            toast.error(res.error || "Credenciales inválidas");
            return;
        }
        toast.success(`Bienvenida/o, ${res.user.name}`);
        navigate("/inbox", { replace: true });
    };

    return (
        <div className="h-full w-full flex bg-slate-50">
            {/* Left brand panel */}
            <div className="hidden md:flex flex-col justify-between p-12 w-[42%] text-white"
                 style={{ background: "linear-gradient(155deg, #0F4C81 0%, #082A48 100%)" }}>
                <div className="font-display text-3xl font-extrabold tracking-tight" data-testid="brand-logo-large">
                    ViajaHub
                </div>
                <div>
                    <h1 className="font-display text-4xl font-bold leading-tight" data-testid="login-hero-title">
                        Un único inbox para<br/>cada cliente, cada viaje.
                    </h1>
                    <p className="mt-4 text-slate-200 max-w-md">
                        Traveler Support · Guardias 24/7 · KimKim · Travefy · Email · canales por venir.
                        Todo en el mismo sitio, sin perder contexto.
                    </p>
                    <div className="mt-10 flex gap-3 text-xs">
                        <div className="border border-white/20 rounded-full px-3 py-1">3-paneles</div>
                        <div className="border border-white/20 rounded-full px-3 py-1">Tiempo real</div>
                        <div className="border border-white/20 rounded-full px-3 py-1">Notas internas</div>
                    </div>
                </div>
                <div className="text-xs text-slate-300">© {new Date().getFullYear()} Viaja de Verdad</div>
            </div>

            {/* Right login form */}
            <div className="flex-1 flex items-center justify-center p-6">
                <form
                    onSubmit={onSubmit}
                    className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8"
                    data-testid="login-form"
                >
                    <div className="md:hidden font-display text-2xl font-extrabold text-brand-600 mb-6">
                        ViajaHub
                    </div>
                    <h2 className="font-display text-2xl font-bold text-slate-900">Entrar</h2>
                    <p className="text-sm text-slate-500 mt-1">Usa tu cuenta corporativa.</p>

                    <div className="mt-6 space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="email-input" className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                            <Input
                                id="email-input"
                                data-testid="email-input"
                                type="email"
                                autoComplete="username"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="tu.nombre@viajadverdad.com"
                                className="h-11"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="password-input" className="text-xs uppercase tracking-wider text-slate-500">Contraseña</Label>
                            <div className="relative">
                                <Input
                                    id="password-input"
                                    data-testid="password-input"
                                    type={showPwd ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="h-11 pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd(v => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                                    data-testid="toggle-password-visibility"
                                    tabIndex={-1}
                                >
                                    {showPwd ? <EyeSlash size={18}/> : <Eye size={18}/>}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                                 data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-11 bg-brand-600 hover:bg-brand-700 font-semibold"
                            data-testid="login-submit"
                        >
                            {submitting ? "Entrando…" : "Entrar"}
                        </Button>
                    </div>

                    <p className="text-xs text-slate-400 mt-6 text-center">
                        ¿No tienes cuenta? Habla con un admin de tu equipo.
                    </p>
                </form>
            </div>
        </div>
    );
}
